import { createSignal, Show, type Component } from 'solid-js';

import { runBackfill, runHealthCheck } from '../lib/api.js';

/**
 * The two actions a merchant needs on day one (SPEC FR-1, §3.6).
 *
 * Without an import button the dashboard is empty on install and stays empty — every open invoice
 * already in Shopify is invisible, which is the fastest possible way to lose trust in an app whose
 * whole promise is that the numbers are right. Import is explicit rather than automatic because it
 * changes every figure on the page, and a merchant who did not ask for it would reasonably read
 * that as the app inventing invoices.
 *
 * Busy state uses the button's own `loading` attribute instead of swapping the label to
 * "Importing…": swapping the text reflows the button mid-click and loses the accessible name.
 */
export const Actions: Component<{ onChanged: () => void }> = (props) => {
  const [busy, setBusy] = createSignal<'import' | 'check' | null>(null);
  const [message, setMessage] = createSignal<string | null>(null);
  const [failed, setFailed] = createSignal(false);

  async function run(kind: 'import' | 'check'): Promise<void> {
    setBusy(kind);
    setMessage(null);
    setFailed(false);
    try {
      if (kind === 'import') {
        const result = await runBackfill();
        setMessage(
          result.ingested === 0
            ? `Checked ${result.scanned} recent orders — no net-terms invoices to import.`
            : `Imported ${result.ingested} net-terms order(s) from the last 60 days.`,
        );
      } else {
        const result = await runHealthCheck();
        setMessage(
          result.discrepancies === 0
            ? 'Checked every balance against Shopify — all totals reconcile.'
            : `Found ${result.discrepancies} discrepanc(y/ies); ${result.healed} repaired automatically.`,
        );
      }
      props.onChanged();
    } catch (error) {
      // Say what happened, what to do, and how to reach a human (CLAUDE.md §5).
      setFailed(true);
      setMessage(
        `That did not work: ${String(error)}. Try again, or email support@duebook.app and a human ` +
          'will reply within one business day.',
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <s-section>
      <s-stack direction="block" gap="base">
        <s-stack direction="inline" gap="base">
          <s-button
            loading={busy() === 'import' ? 'true' : undefined}
            disabled={busy() !== null ? 'true' : undefined}
            onClick={() => void run('import')}
          >
            Import last 60 days
          </s-button>
          <s-button
            variant="secondary"
            loading={busy() === 'check' ? 'true' : undefined}
            disabled={busy() !== null ? 'true' : undefined}
            onClick={() => void run('check')}
          >
            Run health check
          </s-button>
        </s-stack>
        <Show when={message()}>
          <s-banner tone={failed() ? 'critical' : 'success'} dismissible>
            <s-paragraph>{message()}</s-paragraph>
          </s-banner>
        </Show>
      </s-stack>
    </s-section>
  );
};
